<template>
  <v-container fluid
               class="pb-0 pr-0"
               style="height: calc(100vh - 64px);">
    <PageTitle icon="fal fa-fw fa-puzzle-piece">
      Legacy Presentation Builder
    </PageTitle>
    <!-- @see https://chatgpt.com/c/1dbf14df-b9c4-4655-a404-8a738a9a430d -->
    <div class="PresentationBuilder__Container">
      <div class="PresentationBuilder__ScrollingColumn PresentationBuilder__PresentationColumn">
        <v-card v-for="presentation in filteredPresentations"
                :key="presentation.id"
                class="mb-4"
                :elevation="presentationID === presentation.id ? 8 : 0"
                @click.prevent="selectPresentation(presentation)">
          <div v-if="presentation.thumbnail"
               class="pa-1 grey d-flex justify-center align-center">
            <v-img :src="presentation.thumbnail.url"
                   :alt="presentation.name"
                   contain
                   max-width="130"
                   class="PresentationBuilder__Thumbnail">
            </v-img>
          </div>
          <div class=" text-caption pa-2">
            {{ presentation.name }}
          </div>
        </v-card>
      </div>
      <v-divider class="mr-2" vertical></v-divider>
      <template v-if="presentationLoading || (presentation && filteredFiles.length)">
        <div class="PresentationBuilder__ScrollingColumn PresentationBuilder__SlideColumn">
          <div v-if="presentationLoading"
               class="d-flex justify-center flex-1">
            <v-progress-circular indeterminate
                                 size="80">
            </v-progress-circular>
          </div>
          <template v-else>
            <div class="d-flex flex-column">
              <v-btn @click.prevent="addAll"
                     class="mb-2"
                     block>
                Add all
              </v-btn>
              <v-btn @click.prevent="removeAll"
                     class="mb-2"
                     block>
                Remove all
              </v-btn>
            </div>
            <div>
              <v-card v-for="file in filteredFiles"
                      :key="file.id"
                      class="mb-4">
                <div class="pa-1 grey d-flex justify-center align-center">
                  <v-img :src="file.url"
                         :alt="file.name"
                         contain
                         max-width="130"
                         class="PresentationBuilder__Thumbnail"
                         @click.prevent="toggleFile(file)">
                  </v-img>
                </div>
                <v-card-text v-if="file.is_required_slide"
                             class="py-0 px-2 text-center">
                  <div class="font-weight-bold red--text text-uppercase">
                    Required slide
                  </div>
                </v-card-text>
                <div class="pa-2">
                  <v-btn v-if="fileIncluded(file)"
                         color="primary"
                         :disabled="file.is_required_slide"
                         @click.prevent="removeFile(file)"
                         block>
                    Remove
                  </v-btn>
                  <v-btn v-else
                         @click.prevent="addFile(file, true)"
                         block>
                    Add
                  </v-btn>
                </div>
              </v-card>
            </div>
          </template>
        </div>
        <v-divider class="mr-2" vertical></v-divider>
        <div class="PresentationBuilder__ThirdColumn">
          <template v-if="form.files.length">
            <div class="d-flex align-start mt-1 mr-2">
              <v-text-field v-model="form.name"
                            label="Name your custom presentation"
                            outlined
                            dense
                            hint="The name has to be unique"
                            @keydown.enter.prevent="dialog = true"
                            maxlength="200"
                            class="mr-4">
              </v-text-field>
              <v-dialog v-model="dialog"
                        max-width="800">
                <template v-slot:activator="{on, attrs}">
                  <v-btn color="primary"
                         :disabled="!form.name"
                         class="mt-1"
                         v-bind="attrs"
                         v-on="on">
                    Save
                  </v-btn>
                </template>
                <v-form @submit.prevent="submit"
                        :disabled="form.busy">
                  <v-card>
                    <v-card-title class="text-h5 grey lighten-2">
                      Save Custom Presentation
                    </v-card-title>
                    <div class="pa-4">
                      <v-text-field v-model="form.name"
                                    label="Name"
                                    outlined
                                    required
                                    :rules="[rules.required]"
                                    :error-messages="form.errors.get('name')">
                      </v-text-field>

                      <v-autocomplete v-if="1===2"
                                      v-model="form.folder_id"
                                      label="Folder"
                                      outlined
                                      placeholder="Select folder"
                                      prepend-icon="fal fa-folder-tree"
                                      :items="foldersTree"
                                      item-value="id"
                                      item-text="name"
                                      clearable
                                      :error-messages="form.errors.get('folder_id')">
                        <template v-slot:item="{item: folder}">
                          <div class="d-flex">
                            <div v-for="ancestor in folder.ancestors"
                                 :key="ancestor.id"
                                 class="d-flex px-2">
                            </div>
                            <v-icon left
                                    v-if="form.folder_ids.includes(folder.id)">
                              fal fa-folder-open
                            </v-icon>
                            <v-icon left
                                    v-else>
                              fal fa-folder
                            </v-icon>
                            <div>
                              {{ folder.name }}
                            </div>
                          </div>
                        </template>
                      </v-autocomplete>

                      <presentation-form-labels v-if="1===2"
                                                :form="form">
                      </presentation-form-labels>

                      <div class="d-flex">
                        <v-switch v-model="form.is_favorite"
                                  label="Add to favorites"
                                  class="mt-0">
                        </v-switch>
                      </div>

                      <div>
                        It will only be visible to you unless an administrator makes it available to your colleagues.
                      </div>
                    </div>
                    <v-card-actions class="px-4">
                      <v-btn type="submit"
                             color="primary"
                             :disabled="form.busy">
                        Save
                      </v-btn>
                      <v-btn type="button"
                             @click.prevent="dialog = false"
                             :disabled="form.busy">
                        Cancel
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-form>
              </v-dialog>
            </div>
            <v-divider class="py-2 mr-2"></v-divider>

            <draggable v-model="form.files"
                       class="PresentationBuilder__SlideArea">
              <v-card v-for="(file, index) in form.files"
                      :key="file.id"
                      class="PresentationBuilder__Slide PresentationBuilder__Draggable">
                <div class="pa-1 grey fill-height d-flex align-center">
                  <div class="d-flex flex-column fill-height flex-grow-1 justify-center align-center">
                    <div class="d-flex flex-column justify-center flex-grow-1 fill-height">
                      <v-img :src="file.url"
                             :alt="file.name"
                             contain
                             width="260">
                      </v-img>
                    </div>
                    <div class="d-flex justify-space-between align-center pt-2"
                         style="width: 100%;">
                      <v-icon>
                        fal fa-fw fa-grip-vertical
                      </v-icon>
                      <div class="text-center white--text font-weight-bold"
                           style="flex-grow: 1; flex-shrink: 0;">
                        {{ (index + 1) }}
                      </div>
                      <v-btn v-if="file.is_required_slide === false"
                             icon
                             @click.prevent="removeFile(file)">
                        <v-icon>
                          fal fa-fw fa-trash
                        </v-icon>
                      </v-btn>
                      <v-btn v-else
                             icon
                             @click.prevent=""
                             color="red">
                        <v-icon color="red"
                                title="This is a required slide.">
                          fal fa-fw fa-asterisk
                        </v-icon>
                      </v-btn>
                    </div>
                  </div>
                  <v-btn icon
                         v-if="1===2"
                         class="ml-2"
                         @click.prevent="removeFile(file)">
                    <v-icon>
                      fal fa-fw fa-trash
                    </v-icon>
                  </v-btn>
                </div>
              </v-card>
            </draggable>

          </template>
          <div v-else class="fill-height d-flex justify-center align-center">
            Select slides on the left to add them to your custom presentation.
          </div>
        </div>
      </template>
    </div>
  </v-container>
</template>
<script>
import {Form} from 'vform';
import {mapGetters} from 'vuex';
import presentationService from '../../../../ml/services/presentationService';
import presentationFileService from '../../../../ml/services/presentationFileService';
import {find, isObject, merge} from 'lodash';
import draggable from 'vuedraggable';
import formValidation from '../../plugins/formValidation';
import folderService from '../../../../ml/services/folderService';
import PresentationFormLabels from '../presentations/components/PresentationFormLabels';
import PageTitle from '../../components/PageTitle.vue';

export default {
  components: {PageTitle, PresentationFormLabels, draggable},
  mixins: [formValidation],
  data() {
    return {
      addAllFiles: false,
      dialog: false,
      form: new Form(merge(presentationService.presentationFormFields(), {
        files: [],
        items: [],
        presentation_type: 'PDF',
        is_custom: true, // MLD-3281
        is_favorite: true,
        settings: {
          can_be_presented_by_user: true,
          can_be_shared: true,
          can_be_shared_link: true,

          // User permissions
          can_be_exported: true, // MLD-3281
          can_be_printed_by_user: true, // MLD-3281
          can_be_used_in_builder: true, // MLD-3874

          // Recipient permissions
          can_be_printed: true, // MLD-3281
          can_be_downloaded: true, // MLD-3281
          can_be_forwarded: false, // MLD-3281
        }
      })),
      presentationLoading: null,
      presentationID: '',
      presentation: null,
      files: [],
      foldersTree: [],
      slideContainerHeight: '',
    }
  },
  computed: {
    ...mapGetters([
      'team',
      'user',
    ]),
    ...mapGetters('content', [
      'folders',
      'presentations',
      'SORT_OPTION_DATE',
      'SORT_OPTION_NAME',
    ]),
    filteredPresentations() {
      return this.presentations.filter(p => {
        if (!p.settings.can_be_used_in_builder) { // MLD-2932
          //console.log(`Presentation ${p.id} cannot be used in the builder`);
          return false;
        }
        return p.type_pdf || p.converts_to_pdf;
      });
    },
    filteredFiles() {
      if (!this.presentation) {
        return null;
      }
      return this.files;
    },
    requiredSlides() { // MLD-5247
      return this.files.filter(f => f.is_required_slide);
    },
    hasRequiredSlides() { // MLD-5247
      return this.requiredSlides.length > 0;
    },
  },
  created() {
    this.fetchData();
  },
  methods: {

    fetchData() {
      folderService.getFlatTree()
        .then(data => {
          this.foldersTree = data;
        })
    },
    async selectPresentation(p) {
      this.addAllFiles = false;
      if (isObject(p)) {
        this.presentationID = p.id;
      }
      this.presentationLoading = p.id;
      this.presentation = await presentationService.getPresentation(this.presentationID);
      this.files = await presentationFileService.getLegacySlides(this.presentation.id); // MLD-5247
      this.presentationLoading = null;
    },
    toggleFiles() {
      if (this.addAllFiles) {
        this.addAll();
      } else {
        this.removeAll()
      }
    },
    addAll() {
      if (!this.presentation) {
        return;
      }
      this.filteredFiles.forEach(file => {
        this.addFile(file, false);
      });
    },
    removeAll() {
      if (!this.presentation) {
        return;
      }
      this.files.forEach(this.removeFile);
    },
    addFile(file, includeRequired) {
      if (this.fileIncluded(file)) {
        // File already included.
        return;
      }
      this.form.files.push(file);

      // MLD-5247
      if (includeRequired && file.is_required_slide === false && this.hasRequiredSlides) {
        this.requiredSlides.forEach(f => {
          this.addFile(f, false);
        });
        //this.$toast.success('The required slide(s) were added.');
      }
    },
    toggleFile(file) {
      if (this.fileIncluded(file)) {
        if (file.is_required_slide === false) {
          this.removeFile(file);
        }
      } else {
        this.addFile(file, true);
      }
    },
    removeFile(file) {
      if (!this.fileIncluded(file)) {
        // File is not already included.
        return;
      }
      /*if (file.is_required_slide) { // MLD-5247
        this.$toast.error('Required slides cannot be removed.');
        return;
      }*/
      this.form.files = this.form.files.filter(f => f.id !== file.id);
    },
    fileIncluded(file) {
      return !!find(this.form.files, f => f.id === file.id);
    },
    submit() {
      if (this.form.busy) {
        return;
      }
      this.form.owner_id = this.user.id;
      this.form.folder_ids = this.form.folder_id ? [this.form.folder_id] : [];
      this.form.items = this.form.files.map(file => {
        return {
          file_id: file.id,
          page: file.page,
          presentation_id: file.presentationID,
        };
      });
      this.form.post(`/api/presentations/legacy/build`)
        .then(response => {
          const presentationID = response.data.presentationID;
          this.$store.dispatch('content/fetchPresentations');
          if (presentationID && this.form.is_favorite) {
            this.$store.dispatch('content/toggleBookmark', presentationID);
          }
          // Change their sort option so the new presentation will appear at the top of their screen.
          this.$store.dispatch('content/setSortOption', this.SORT_OPTION_DATE);

          this.$store.commit('setSuccessMessage', 'We are building your custom presentation now!. It will appear in your content library shortly.');
          this.form.finishProcessing();
          this.dialog = false;
          this.$store.dispatch('content/setActiveFolderID', this.form.folder_id);
          this.$router.push({name: `${this.$root.routePrefix}.home`});
        })
        .catch(err => {
          // TODO: Display an error message.
        })
        .finally(() => {
        })
    }
  }
}
</script>
